import {
  ChatMessage,
  type ChatMessageProps,
  type Message,
} from "@/components/ui/chat-message";
import { ChatFeedback } from "@/components/ui/chat-feedback";

type AdditionalMessageOptions = Omit<ChatMessageProps, keyof Message>;

interface MessageListProps {
  messages: Message[];
  conversationId?: string;
  showTimeStamps?: boolean;
  isTyping?: boolean;
  isGenerating?: boolean;
  onRedo?: (assistantMessageId: string) => void;
  messageOptions?:
    | AdditionalMessageOptions
    | ((message: Message) => AdditionalMessageOptions);
}

export function MessageList({
  messages,
  conversationId,
  showTimeStamps = true,
  isTyping = false,
  isGenerating = false,
  onRedo,
  messageOptions,
}: MessageListProps) {
  const latestAssistantMessage = [...messages]
    .reverse()
    .find((m) => m.role === "assistant");

  const showTypingIndicator =
    isTyping &&
    (!latestAssistantMessage ||
      latestAssistantMessage.content.trim().length === 0);

  return (
    <div className="space-y-4 overflow-visible">
      {messages.map((message, index) => {
        let additionalOptions: AdditionalMessageOptions | undefined;

        if (messageOptions) {
          additionalOptions =
            typeof messageOptions === "function"
              ? messageOptions(message)
              : messageOptions;
        } else if (
          conversationId &&
          message.role === "assistant" &&
          (message as any).backendId &&
          !(isGenerating && index === messages.length - 1)
        ) {
          additionalOptions = {
            actions: (
              <ChatFeedback
                conversationId={conversationId}
                messageId={(message as any).backendId as string}
                onRedo={
                  onRedo
                    ? () => onRedo(message.backendId ?? (message.id as string))
                    : undefined
                }
              />
            ),
          };
        }

        return (
          <div
            key={index}
            className={`flex w-full ${
              message.role === "user" ? "justify-end" : "items-start gap-2"
            }`}
          >
            {/* AI Avatar */}
            {message.role === "assistant" &&
              message.content.trim().length > 0 && (
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                  <img
                    src="/Logo.svg"
                    alt="AI Assistant Avatar"
                    className="w-5 h-5 object-contain"
                  />
                </div>
              )}

            <div className="max-w-[85%]">
              <ChatMessage
                showTimeStamp={showTimeStamps}
                {...message}
                {...additionalOptions}
              />
            </div>
          </div>
        );
      })}
      {showTypingIndicator && (
        <div className="flex w-full items-start gap-2">
          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
            <img
              src="/Logo.svg"
              alt="AI Assistant Avatar"
              className="w-5 h-5 object-contain"
            />
          </div>
          <div className="bg-white rounded-md py-2 px-3 max-w-[85%] w-full">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 text-gray-400 animate-spin border-2 border-gray-300 border-t-gray-600 rounded-full" />
              <span className="text-sm text-gray-600">thinking...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
